<lib-cdw-dialog
  [toolbarTitle]="emailExtractionMode ? 'Add Users via CSV' : 'Import a CSV File'"
  [primaryButtonText]="showWelcomeScreen ? 'Continue' : continueButtonLabel"
  [secondaryButtonText]="'Cancel'"
  [buttonAlignment]="'justify-end'"
  [customWidth]="'685px'"
  [customHeight]="dialogHeight"
  [primaryButtonDisabled]="!showWelcomeScreen && isContinueDisabled"
  (primaryButtonClick)="handleContinue()"
  (secondaryButtonClick)="cancel()"
  (dismissButtonClick)="cancel()">

  <!-- Welcome Screen Content -->
  <div *ngIf="showWelcomeScreen" class="flex flex-col h-full w-full gap-3 justify-center">
    <h3 class="welcome-title">Here's what to expect:</h3>

    <div class="flex w-full mb-6 step-container">
      <div class="flex flex-col items-center gap-2 pr-4 font-normal" style="width: 33.33%;">
        <lib-cdw-step-indicator [stepNumber]="1" [size]="'small'" [enabledColor]="ctaBlue"></lib-cdw-step-indicator>
        <p class="welcome-steps">{{ emailExtractionMode
          ? 'Upload a CSV file containing email addresses. The file only needs an Email column. You can download a template below.'
          : 'Upload a CSV file. It needs to be formatted with user collections and attached users. You can download a template below.'
        }}</p>
      </div>

      <div class="flex flex-col items-center gap-2 px-3 font-normal" style="width: 33.33%;">
        <lib-cdw-step-indicator [stepNumber]="2" [size]="'small'" [enabledColor]="ctaBlue"></lib-cdw-step-indicator>
        <p class="welcome-steps">We'll validate the file and provide an opportunity for you to <strong>correct any errors and see warnings</strong>.</p>
      </div>

      <div class="flex flex-col items-center gap-2 pl-3 font-normal" style="width: 33.33%;">
        <lib-cdw-step-indicator [stepNumber]="3" [size]="'small'" [enabledColor]="ctaBlue"></lib-cdw-step-indicator>
        <p class="welcome-steps">{{ emailExtractionMode ? 'The users are added to the collection.' : 'The data is imported.' }}</p>
      </div>
    </div>

    <div class="template-download-section">
      <a href="#" (click)="downloadTemplate(); $event.preventDefault()" class="text-sm hover:underline font-normal">
        {{ emailExtractionMode
          ? 'Download a pre-filled CSV file with example email addresses. Replace the data with yours.'
          : 'Download a pre-filled CSV file with example data of user collections and users. Replace the data with yours.'
        }}
      </a>
    </div>
  </div>

  <!-- Step Content -->
  <div *ngIf="!showWelcomeScreen" class="flex flex-col h-full w-full p-4">
    <!-- Step Indicators -->
    <div class="flex justify-center items-center gap-4 mb-6">
      <div class="flex items-center gap-2 cursor-pointer" (click)="goToStep(1)">
        <lib-cdw-step-indicator
          [stepNumber]="1"
          [size]="'small'"
          [enabled]="true"
          [enabledColor]="currentStep === 1 ? ctaBlue : gray300"
          [disabledColor]="gray300">
        </lib-cdw-step-indicator>
        <span class="step-label">Upload file</span>
      </div>

      <div class="flex-1 h-px bg-gray-300"></div>

      <div class="flex items-center gap-2 cursor-pointer" (click)="goToStep(2)">
        <lib-cdw-step-indicator
          [stepNumber]="2"
          [size]="'small'"
          [enabled]="true"
          [enabledColor]="currentStep === 2 ? ctaBlue : gray300"
          [disabledColor]="gray300">
        </lib-cdw-step-indicator>
        <span class="step-label">Validation</span>
      </div>

      <div class="flex-1 h-px bg-gray-300"></div>

      <div class="flex items-center gap-2 cursor-pointer" (click)="goToStep(3)">
        <lib-cdw-step-indicator
          [stepNumber]="3"
          [size]="'small'"
          [enabled]="true"
          [enabledColor]="currentStep === 3 ? ctaBlue : gray300"
          [disabledColor]="gray300">
        </lib-cdw-step-indicator>
        <span class="step-label">Import</span>
      </div>
    </div>

    <!-- Step 1: Upload CSV -->
    <div *ngIf="currentStep === 1" class="flex !h-full !w-full">
        <lib-cdw-file-upload
          class="flex !h-full !w-full"
          type="box"
          accept=".csv"
          dragMessage="Drag your file into this box"
          buttonText="Select file"
          buttonLevel="secondary"
          [showIcon]="false"
          dragMessageFontFamily="Roboto"
          dragMessageFontSize="26px"
          dragMessageFontWeight="600"
          [dragMessageColor]="gray200"
          selectedFileTextFontFamily="Roboto"
          selectedFileTextFontSize="14px"
          selectedFileTextFontWeight="700"
          buttonLabelFontFamily="Roboto"
          buttonLabelFontSize="14px"
          buttonLabelFontWeight="700"
          [buttonLabelColor]="ctaBlue"
          [boxBorderColor]="gray300"
          [boxBackgroundColor]="gray50"
          [isUploading]="isUploading"
          uploadingText="Uploading..."
          [uploadingTextColor]="gray200"
          [selectedFile]="uploadedFile"
          (fileSelected)="onFileSelected($event)">
        </lib-cdw-file-upload>
    </div>

    <!-- Step 2: Validate -->
    <div *ngIf="currentStep === 2" class="flex flex-col gap-4 h-full">
      <h2 class="text-gray-400 font-heading text-[22px] font-extrabold mb-2">Errors and warnings</h2>

      <div *ngIf="isValidating" class="flex flex-col items-center gap-4 py-12">
        <mat-spinner diameter="40"></mat-spinner>
        <p>Validating your file...</p>
      </div>

      <div *ngIf="!isValidating" class="flex flex-col gap-6 max-h-[515px] overflow-y-auto">
        <!-- Errors Section -->
        <div class="flex flex-col">
          <div class="bg-tertiary-blue-light px-2 py-1.5">
            <h3 class="text-cdw-red-corp-dark-bg font-bold text-base font-roboto">DATA ERRORS</h3>
          </div>
          <div class="bg-white border-2 border-gray-300 rounded">
            <!-- Show errors if any -->
            <div *ngIf="validationErrors.length > 0">
              <div *ngFor="let error of validationErrors" class="px-2.5 py-3 border-b border-gray-200 last:border-b-0">
                <p class="text-cdw-red-corp-dark-bg font-roboto text-base font-normal">{{ error }}</p>
              </div>
            </div>
            <!-- Show success message if no errors -->
            <div *ngIf="validationErrors.length === 0" class="px-2.5 py-3 flex items-center gap-2">
              <lib-cdw-icon icon="done_outline" [customColor]="greenDark" [useMatIcon]="true" [outlined]="false"></lib-cdw-icon>
              <p class="text-tertiary-green-dark font-roboto text-base font-normal">No data errors found. You may continue with the import.</p>
            </div>
          </div>
        </div>

        <!-- Warnings Section -->
        <div *ngIf="validationWarnings.length > 0 && validationErrors.length === 0" class="flex flex-col">
          <div class="bg-tertiary-blue-light px-2 py-1.5">
            <h3 class="text-cdw-red-corp-dark-bg font-bold text-base font-roboto">DATA WARNINGS</h3>
          </div>
          <div class="bg-white px-2.5 py-2.5 border-2 border-gray-300 rounded">
            <div *ngFor="let warning of validationWarnings" class="mb-4 last:mb-0">
              <p class="text-gray-400 font-roboto text-base font-normal mb-4">{{ warning.message }}</p>
              <!-- Radio buttons for existing collection warning -->
              <div *ngIf="warning.type === 'existing_collections'" class="flex flex-col gap-4 px-20">
                <label class="flex items-center gap-3 px-2.5 py-2.5 border-2 rounded cursor-pointer"
                  [class.border-blue-500]="importMode === 'append'"
                  [class.bg-blue-50]="importMode === 'append'"
                  [class.border-gray-300]="importMode !== 'append'"
                  [class.bg-white]="importMode !== 'append'"
                  [class.hover:bg-gray-50]="importMode !== 'append'">
                  <input type="radio" name="importMode" value="append" [(ngModel)]="importMode" class="w-4 h-4">
                  <span class="text-gray-400 font-roboto text-sm font-normal">Append new users to the existing User Collection(s)</span>
                </label>
                <label class="flex items-center gap-3 px-2.5 py-2.5 border-2 rounded cursor-pointer"
                  [class.border-blue-500]="importMode === 'replace'"
                  [class.bg-blue-50]="importMode === 'replace'"
                  [class.border-gray-300]="importMode !== 'replace'"
                  [class.bg-white]="importMode !== 'replace'"
                  [class.hover:bg-gray-50]="importMode !== 'replace'">
                  <input type="radio" name="importMode" value="replace" [(ngModel)]="importMode" class="w-4 h-4">
                  <span class="text-gray-400 font-roboto text-sm font-normal">Replace the User Collection(s) with the one(s) in the CSV</span>
                </label>
              </div>

              <!-- Radio buttons for description mismatch warning -->
              <div *ngIf="warning.type === 'description_mismatch'" class="flex flex-col gap-4">
                <div class="flex flex-col gap-4 px-20">
                  <label class="flex items-center gap-3 px-2.5 py-2.5 border-2 rounded cursor-pointer"
                    [class.border-blue-500]="descriptionUpdateMode === 'update'"
                    [class.bg-blue-50]="descriptionUpdateMode === 'update'"
                    [class.border-gray-300]="descriptionUpdateMode !== 'update'"
                    [class.bg-white]="descriptionUpdateMode !== 'update'"
                    [class.hover:bg-gray-50]="descriptionUpdateMode !== 'update'">
                    <input type="radio" name="descriptionUpdateMode" value="update" [(ngModel)]="descriptionUpdateMode" class="w-4 h-4">
                    <span class="text-gray-400 font-roboto text-sm font-normal">Update description(s) with the one(s) from the CSV</span>
                  </label>
                  <label class="flex items-center gap-3 px-2.5 py-2.5 border-2 rounded cursor-pointer"
                    [class.border-blue-500]="descriptionUpdateMode === 'keep'"
                    [class.bg-blue-50]="descriptionUpdateMode === 'keep'"
                    [class.border-gray-300]="descriptionUpdateMode !== 'keep'"
                    [class.bg-white]="descriptionUpdateMode !== 'keep'"
                    [class.hover:bg-gray-50]="descriptionUpdateMode !== 'keep'">
                    <input type="radio" name="descriptionUpdateMode" value="keep" [(ngModel)]="descriptionUpdateMode" class="w-4 h-4">
                    <span class="text-gray-400 font-roboto text-sm font-normal">Keep the current description(s)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 3: Import -->
    <div *ngIf="currentStep === 3" class="h-full">
        <div class="flex flex-col items-center justify-center gap-6 h-full justify-center">
          <p class="m-0 font-heading text-[22px] font-extrabold" [style.color]="gray200">Importing your data...</p>
          <lib-cdw-spinner type="apple" size="large"></lib-cdw-spinner>
        </div>
    </div>
  </div>
</lib-cdw-dialog>
